import { useMemo } from "react";
import { createFleetCheckRowBuilder, FleetCheckRow } from "../models/fleetCheckRow";
import { useCheckDiagnosticIds } from "./useCheckDiagnosticIds";
import { useFleetStatusFeed } from "./useFleetStatusFeed";
import { useAddInState } from "@/components/AddInStateProvider";
import { RESULTS_LIMIT_DEVICE } from "@/constants/resultsLimits";
import { useDecodeVins } from "@/hooks/devices/useDecodeVins";
import {
  DEVICE_FIELDS_WITH_FIRMWARE,
  useDevicesBySearchWithNumDevices,
} from "@/hooks/devices/useDevicesBySearch";
import { useGroups } from "@/hooks/groups/useGroups";
import { useStatusInfosByIds } from "@/hooks/statusInfos/useStatusInfosByIds";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";
import { LiveDataRow } from "@/pages/live-data/models/LiveDataRow";
import { groupIdsWithDescendants } from "@/utilities/groupIndex";

/** A scanned vehicle that reported nothing; one shared array so its line is not rebuilt. */
const NO_READINGS: LiveDataRow[] = [];
const NO_DIAGNOSTICS: string[] = [];

const LIST_REFETCH_MS = 10 * 60 * 1000;
const STATUS_REFETCH_MS = 2 * 60 * 1000;

/**
 * The fleet-wide check's lines: the vehicles, what the scan read from them, whether their units
 * are communicating and what their VINs decode to.
 *
 * Works without a group filter too: vehicles that sit in no group are only reachable that way.
 *
 * @param search - Already debounced, because every change asks Geotab for the device list again.
 * @param hoursBack - How far back a scan looks.
 * @param withGpsOdometer - Also reads Geotab's GPS odometer, at one call per vehicle.
 * @param onlyIssues - Leaves out vehicles that are complete or have not been scanned.
 */
export function useFleetCheckRows(
  search: string,
  hoursBack: number,
  onlyIssues: boolean,
  withGpsOdometer = false,
) {
  const {
    data,
    isLoading,
    isError: devicesError,
  } = useDevicesBySearchWithNumDevices(
    search,
    false,
    RESULTS_LIMIT_DEVICE,
    DEVICE_FIELDS_WITH_FIRMWARE,
    // The fleet's list does not change minute to minute, and refetching it pulls every vehicle
    // through the same request queue the scan is using.
    LIST_REFETCH_MS,
  );
  // The search stops at the cap, so a fleet that reaches it is not fully listed.
  const isTruncated = (data?.total ?? 0) >= RESULTS_LIMIT_DEVICE;
  const devices = useMemo(() => (data?.devices ?? []) as DeviceWithVehicleInfo[], [data]);

  const deviceIds = useMemo(() => devices.map(device => device.id).sort(), [devices]);

  // Whether each unit is talking to Geotab right now. Independent of the scan: it says whether a
  // vehicle that reported nothing is silent or merely was not driven.
  // Asked rarely: the page wants to know whether a unit talks at all, not what it is doing right
  // now, and this is one request per two thousand vehicles.
  const {
    data: statusInfo,
    isLoading: statusInfoLoading,
    isPlaceholderData: statusInfoPlaceholder,
    isError: statusInfoError,
  } = useStatusInfosByIds(deviceIds, [], STATUS_REFETCH_MS);
  const statusInfoMap = useMemo(() => statusInfo ?? {}, [statusInfo]);

  // Asked only once the list and the unit status are in. The client sends queued requests as one
  // `ExecuteMultiCall`, and the catalog searches are slow: sent together with the device list, the
  // list waited for them and the page stayed empty for a long time.
  const {
    data: checkDiagnostics,
    isLoading: diagnosticsLoading,
    isError: diagnosticsError,
  } = useCheckDiagnosticIds(!isLoading && !statusInfoLoading);
  const groupIds = useScanGroupIds();
  const { rowsByDeviceId, scan, start, stop, lookupsLoading, lookupsError } = useFleetStatusFeed(
    deviceIds,
    checkDiagnostics?.ids ?? NO_DIAGNOSTICS,
    hoursBack,
    groupIds,
    withGpsOdometer,
  );

  // Fixed properties of the vehicle, decoded once and unaffected by the scan window.
  const vins = useMemo(
    () =>
      devices
        .map(device => device.vehicleIdentificationNumber)
        .filter((vin): vin is string => !!vin),
    [devices],
  );
  const { data: vinDetails = [], isLoading: vinsLoading, isError: vinsError } = useDecodeVins(vins);
  const vinDecodedByVin = useMemo(
    () =>
      new Map(
        vinDetails.map(detail => [
          detail.vin,
          [detail.make, detail.model].filter(Boolean).join(" "),
        ]),
      ),
    [vinDetails],
  );

  // Kept across refreshes: it is what lets a refresh redo only the vehicles the scan touched.
  const buildRows = useMemo(createFleetCheckRowBuilder, []);

  const rows = useMemo<FleetCheckRow[]>(
    () =>
      buildRows(
        devices.map(device => ({
          device,
          rows: scan.scannedIds.has(device.id) ? (rowsByDeviceId[device.id] ?? NO_READINGS) : null,
          isCommunicating: statusInfoMap[device.id]?.isDeviceCommunicating,
          vinDecoded: device.vehicleIdentificationNumber
            ? vinDecodedByVin.get(device.vehicleIdentificationNumber)
            : undefined,
        })),
        // "Not scanned" is not an issue -- nobody has looked yet.
      ).filter(row => !onlyIssues || (row.status !== "ok" && row.status !== "notScanned")),
    [
      buildRows,
      devices,
      rowsByDeviceId,
      statusInfoMap,
      vinDecodedByVin,
      scan.scannedIds,
      onlyIssues,
    ],
  );

  return {
    rows,
    deviceCount: deviceIds.length,
    isLoading,
    isTruncated,
    scan,
    start,
    stop,
    /** The catalog search for the diagnostics to read; the scan cannot start before it answers. */
    diagnosticsLoading: diagnosticsLoading || (!checkDiagnostics && !diagnosticsError),
    diagnosticsTruncated: checkDiagnostics?.isTruncated ?? false,
    lookupsLoading,
    /** Unit status or VIN decoding still loading: their columns are not filled yet. */
    // Placeholder status data is the previous list's; the new vehicles are not looked up yet. With
    // no vehicles listed the lookup is disabled and would keep that placeholder forever.
    enrichmentLoading:
      (deviceIds.length > 0 && (statusInfoLoading || statusInfoPlaceholder)) || vinsLoading,
    errors: {
      devices: devicesError,
      statusInfo: statusInfoError,
      vins: vinsError,
      lookups: lookupsError,
      // A failed background refetch keeps the list it had, and that list is still usable.
      diagnostics: diagnosticsError && !checkDiagnostics,
    },
  };
}

/**
 * The groups the scan's series are narrowed to, with all their descendants, as the tachograph
 * page does: the device list includes the vehicles of subgroups, and `DeviceSearch.groups` is not
 * documented to.
 *
 * Fleet-wide (`undefined`) without a plain group selection, or without a complete group tree to
 * add the subgroups from. That reads more than needed but drops no vehicle; readings of vehicles
 * outside the list are dropped by the scan.
 */
function useScanGroupIds(): string[] | undefined {
  const { groups } = useAddInState();
  const groupTree = useGroups();
  const hasGroupSelection = !!groups && groups.length > 0;
  const groupTreeComplete = !groupTree.isError && !groupTree.isTruncated && !!groupTree.data;

  return useMemo(
    () =>
      hasGroupSelection && groupTreeComplete
        ? groupIdsWithDescendants(
            groups.map(group => group.id),
            groupTree.data,
          )
        : undefined,
    [hasGroupSelection, groupTreeComplete, groups, groupTree.data],
  );
}
